import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUILabelTranslation } from '~/data/enums';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { useEnabledModuleTabPaths } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/useEnabledModuleTabPaths';
import { usePrepModuleTabProps } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/usePrepModuleTabProps';
import {
  EmployeeModuleRouting_Performance,
  EmployeeModuleTabPath_Performance,
} from '~/scenes/PortalViews/Views/EmployeeViews/Modules/PerformanceModule/data';
import {
  EmployeeModuleTabAccessMode,
  EmployeeModuleTabProps,
  EmployeeModuleTabSetupProps,
} from '~/scenes/PortalViews/Views/EmployeeViews/Modules/types';

type PropsType = {
  hasFullAccess: boolean;
  modulePath: string;
};

type UseEmployeePerformanceModuleOutput<TabPath> = {
  isLoading: boolean;
  employeeId: number;
  enabledTabPaths: TabPath[];
  defaultTab: TabPath;
  modulePath: string;
  tabs: EmployeeModuleTabProps<TabPath>[];
};

export const useEmployeePerformanceModule = (
  props: PropsType,
): UseEmployeePerformanceModuleOutput<EmployeeModuleTabPath_Performance> => {
  const { hasFullAccess, modulePath } = props;

  const { t } = useTranslation();
  const { uiLabelTranslation } = useUILabelTranslation();

  const { selectedEmployeeId } = useSelectedEmployeeId();

  const { availableTabs } = useMemo(() => {
    const allTabs = Object.values(EmployeeModuleTabPath_Performance);

    // NOTE: No conditions yet, but likely added later
    const tabConditions: {
      [key in EmployeeModuleTabPath_Performance]?: boolean;
    } = {
      // [EmployeeModuleTabPath_People.DecisionRegistry]:
      // hasData &&
      // applicationConfig?.fComConfig?.miscellaneousConfig
      //   ?.fComEnableDecisionRegistry,
    };

    const availableTabs: EmployeeModuleTabPath_Performance[] = hasFullAccess
      ? allTabs.filter((path) => {
          return Object.keys(tabConditions).includes(path)
            ? tabConditions[path]
            : true;
        })
      : [];

    return { tabConditions, availableTabs };
  }, [hasFullAccess]);

  const tabProps: EmployeeModuleTabSetupProps<EmployeeModuleTabPath_Performance>[] =
    useMemo(() => {
      return [
        {
          id: EmployeeModuleTabPath_Performance.GoalSetting,
          title: t('laGoalSetting'),
        },
        {
          id: EmployeeModuleTabPath_Performance.GoalEvaluation,
          title: uiLabelTranslation('laGoalEvaluation'),
        },
        {
          id: EmployeeModuleTabPath_Performance.SelfEvaluation,
          title: t('laSelfEvaluation'),
        },
        {
          id: EmployeeModuleTabPath_Performance.Feedback,
          title: t('laFeedback'),
        },
        {
          id: EmployeeModuleTabPath_Performance.PerformanceEvaluation,
          title: t('laPerformanceEvaluation'),
        },
        {
          id: EmployeeModuleTabPath_Performance.Notes,
          title: t('laNotes'),
        },
        {
          id: EmployeeModuleTabPath_Performance.StatusDiscussion,
          title: t('laStatusDisc'),
        },
        {
          id: EmployeeModuleTabPath_Performance.AppraisalEvaluation,
          title: uiLabelTranslation('laAppraisalEvaluation'),
        },
      ].map((p) => ({ ...p, accessMode: EmployeeModuleTabAccessMode.Full }));
    }, [t, uiLabelTranslation]);

  const { enabledTabConfigs, enabledTabPaths, defaultTab } =
    useEnabledModuleTabPaths({
      ...EmployeeModuleRouting_Performance,
      availableTabs,
    });

  const { tabs, isLoading: isLoadingTabPrep } = usePrepModuleTabProps({
    ...EmployeeModuleRouting_Performance,
    tabConfigs: enabledTabConfigs,
    modulePath,
    tabsSetupProps: tabProps,
    enabledTabPaths,
  });

  return {
    isLoading: isLoadingTabPrep,
    enabledTabPaths,
    defaultTab,
    modulePath,
    employeeId: selectedEmployeeId,
    tabs,
  };
};
